/**
 * A campaign's review (specification §10). Until the cutoff, its brand and its creators raise
 * issues that may change the settlement. Morcast decides each one and publishes it, with its
 * decision, in a revision of the result. Shows once the first result is published.
 */

import type { DatasetItem } from "@morcast/protocol";
import { type FormEvent, useId, useState } from "react";

import { useSession } from "../auth/session.js";
import { shortHex } from "../components/ChainLinks.js";
import { Select } from "../components/forms.js";
import { SupportLink } from "../components/SupportNote.js";
import { Badge, ErrorMessage, styles } from "../components/ui.js";
import { useAppConfig } from "../config.js";
import { formatUtc } from "../format.js";
import type { PublicCampaign } from "./campaigns.js";
import { type Issue, useMyIssues, useRaiseIssue } from "./issues.js";
import { useCampaignResult } from "./results.js";

export function ReviewCard({ campaign }: { campaign: PublicCampaign }) {
  const result = useCampaignResult(campaign);
  const session = useSession();
  const { supportEmail } = useAppConfig();
  if (!result.data) {
    return null;
  }
  const { review, dataset } = result.data;
  const wallet = session.data?.wallet;
  // The brand, or a creator with an item.
  const participant =
    wallet !== undefined &&
    (wallet === campaign.brand.wallet || dataset.items.some((item) => item.wallet === wallet));
  const open = campaign.status === "funded" && Date.now() < Date.parse(review.issuesUntil);

  return (
    <section aria-labelledby="review-title" className={`mt-6 text-sm ${styles.card}`}>
      <h2 id="review-title" className="text-lg font-semibold tracking-tight">
        Review
      </h2>
      <p className="mt-2 text-zinc-700 dark:text-zinc-300">
        {open ? (
          `Until ${formatUtc(review.issuesUntil)}, the brand and the campaign's creators can raise issues that may change the settlement.`
        ) : supportEmail ? (
          <>
            The review closed {formatUtc(review.issuesUntil)}; write to{" "}
            <SupportLink email={supportEmail} /> about anything else.
          </>
        ) : (
          `The review closed ${formatUtc(review.issuesUntil)}.`
        )}{" "}
        Morcast decides each issue and publishes it with its decision in a revised result.
        Settlement can follow from {formatUtc(review.reviewDeadline)}.
      </p>
      {participant && <Issues campaign={campaign} items={dataset.items} open={open} />}
    </section>
  );
}

function Issues({
  campaign,
  items,
  open,
}: {
  campaign: PublicCampaign;
  items: DatasetItem[];
  open: boolean;
}) {
  const mine = useMyIssues(campaign, true);
  const raise = useRaiseIssue(campaign);
  const [summary, setSummary] = useState("");
  const [about, setAbout] = useState("");
  const aboutId = useId();
  const hintId = useId();
  const length = summary.trim().length;

  function submit(event: FormEvent) {
    event.preventDefault();
    raise.mutate(
      { summary: summary.trim(), ...(about ? { submissionId: Number(about) } : {}) },
      {
        onSuccess: () => {
          setSummary("");
          setAbout("");
        },
      },
    );
  }

  return (
    <>
      {open && (
        <form onSubmit={submit} className="mt-4 space-y-3">
          <div>
            <label id={`${aboutId}-label`} htmlFor={aboutId} className="block font-medium">
              About
            </label>
            <Select
              id={aboutId}
              labelId={`${aboutId}-label`}
              value={about}
              options={[
                { value: "", label: "The whole campaign" },
                ...items.map((item) => ({
                  value: String(item.submissionId),
                  label: `Content ${item.contentId} by ${shortHex(item.wallet)}`,
                })),
              ]}
              onChange={setAbout}
            />
          </div>
          <label className="block font-medium">
            Issue
            <textarea
              rows={3}
              value={summary}
              maxLength={MAX_ISSUE}
              aria-describedby={hintId}
              onChange={(event) => setSummary(event.target.value)}
              className={styles.input}
            />
          </label>
          <div
            id={hintId}
            className="flex flex-wrap justify-between gap-x-4 gap-y-1 text-xs text-zinc-500 dark:text-zinc-400"
          >
            <p>
              At least {MIN_ISSUE} characters. Your issue, your wallet and Morcast's decision are
              published with the result.
            </p>
            <p className="tabular-nums">
              {length.toLocaleString("en-US")} / {MAX_ISSUE.toLocaleString("en-US")}
            </p>
          </div>
          <button
            type="submit"
            disabled={raise.isPending || length < MIN_ISSUE}
            className={styles.primaryButton}
          >
            Raise issue
          </button>
          {raise.isError && <ErrorMessage error={raise.error} />}
        </form>
      )}
      {mine.isError && <ErrorMessage error={mine.error} />}
      {mine.data && mine.data.length > 0 && (
        <div className="mt-6">
          <h3 className="font-medium">Your issues</h3>
          <ul className="mt-2 divide-y divide-zinc-200 dark:divide-white/10">
            {mine.data.map((issue) => (
              <IssueRow key={issue.id} issue={issue} items={items} />
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

/** The shortest and longest issue the API takes, without surrounding spaces. */
const MIN_ISSUE = 10;
const MAX_ISSUE = 2_000;

const OUTCOMES = {
  open: { label: "Open", tone: "amber" },
  upheld: { label: "Upheld", tone: "green" },
  rejected: { label: "Rejected", tone: "grey" },
} as const;

function IssueRow({ issue, items }: { issue: Issue; items: DatasetItem[] }) {
  const item = items.find((entry) => entry.submissionId === String(issue.submissionId));
  const { label, tone } = OUTCOMES[issue.outcome ?? "open"];
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={tone}>{label}</Badge>
        <span className="text-xs text-zinc-500 dark:text-zinc-400">
          {item ? `Content ${item.contentId}` : "Whole campaign"}, raised{" "}
          {formatUtc(issue.raisedAt)}
        </span>
      </div>
      <p className="mt-1">{issue.summary}</p>
      {issue.decision && (
        <p className="mt-1 text-zinc-600 dark:text-zinc-400">
          <span className="font-medium">Decision:</span> {issue.decision}
        </p>
      )}
    </li>
  );
}
